import { useEffect, useState, useCallback } from 'react';
import Sidebar from '../../../components/sidebar/Sidebar';
import '../ServerData.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';

function getToken() { return localStorage.getItem('token'); }
function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function IssueCard({ issue, onUpdate }) {
  const [open, setOpen]           = useState(false);
  const [adminNote, setAdminNote] = useState(issue.adminNote || '');
  const [saving, setSaving]       = useState(false);

  const resolve = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/botdata/issues/${issue._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: 'resolved', adminNote, resolvedById: 'dashboard', resolvedByUsername: 'Dashboard', needsDiscordUpdate: true }),
      });
      if (res.ok) onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/botdata/issues/${issue._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ adminNote }),
      });
      onUpdate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-card">
      <div className="sd-card-header" onClick={() => setOpen(o => !o)}>
        <div className="sd-card-left">
          <div className="sd-card-meta">
            {issue.botId && <span className="sd-bot-pill"><i className="bi bi-robot" />{issue.botId}</span>}
            <span><i className="bi bi-person" /> {issue.memberUsername}</span>
            <span><i className="bi bi-building" /> {issue.guildName || issue.guildId}</span>
            <span><i className="bi bi-clock" /> {formatDate(issue.createdAt)}</span>
          </div>
          <div className="sd-card-content">{issue.title}</div>
        </div>
        <div className="sd-card-actions">
          <span className={`sd-badge sd-badge-${issue.status}`}>{issue.status}</span>
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ color: '#475569', fontSize: '0.8rem' }} />
        </div>
      </div>

      {open && (
        <div className="sd-detail">
          {issue.threadUrl && (
            <div className="sd-field-group">
              <div className="sd-field-label">Thread</div>
              <div className="sd-field-value">
                <a href={issue.threadUrl} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>
                  {issue.threadUrl}
                </a>
              </div>
            </div>
          )}
          {issue.resolvedAt && (
            <div className="sd-field-group">
              <div className="sd-field-label">Resolved</div>
              <div className="sd-field-value">{formatDate(issue.resolvedAt)} by {issue.resolvedByUsername || '—'}</div>
            </div>
          )}
          <div className="sd-field-group">
            <div className="sd-field-label">Admin Note</div>
            <textarea
              className="sd-textarea"
              placeholder="Add a note about this issue..."
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
            />
          </div>
          <div className="sd-action-row">
            {issue.status === 'open' && (
              <button className="sd-btn sd-btn-resolve" onClick={resolve} disabled={saving}>
                <i className="bi bi-check-circle" /> Mark Resolved
              </button>
            )}
            <button className="sd-btn sd-btn-neutral" onClick={saveNote} disabled={saving}>
              <i className="bi bi-sticky" /> Save Note
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IssuesPage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'open' ? '?status=open' : filter === 'resolved' ? '?status=resolved' : '';
      const res = await fetch(`${API}/botdata/issues${params}`, { headers: authHeaders() });
      const data = await res.json();
      setIssues(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="sd-page">
      <Sidebar title="Bot Issues" />

      <div className="sd-header">
        <div className="sd-title">
          <i className="bi bi-exclamation-triangle-fill" />
          Bot Issues
        </div>
        <div className="sd-filters">
          {['all', 'open', 'resolved'].map(f => (
            <button key={f} className={`sd-filter-btn${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <button className="sd-filter-btn" onClick={load}><i className="bi bi-arrow-clockwise" /></button>
        </div>
      </div>

      <div className="sd-stats">
        {[
          { label: 'Total', value: issues.length, color: '#e2e8f0' },
          { label: 'Open', value: issues.filter(i => i.status === 'open').length, color: '#fbbf24' },
          { label: 'Resolved', value: issues.filter(i => i.status === 'resolved').length, color: '#4ade80' },
        ].map(s => (
          <div className="sd-stat-pill" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(4)].map((_, i) => <div key={i} className="sd-skel" style={{ height: 68, borderRadius: 14 }} />)}
        </div>
      ) : issues.length === 0 ? (
        <div className="sd-empty"><i className="bi bi-check-circle" /><p>No issues found</p></div>
      ) : (
        issues.map(issue => <IssueCard key={String(issue._id)} issue={issue} onUpdate={load} />)
      )}
    </div>
  );
}
